"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { Wrapper, Status } from "@googlemaps/react-wrapper";
import styles from "./package-route-map.module.css";

/** Which leg(s) of the trip a stop belongs to. */
export type StopKind = "city" | "cruise" | "both";

export interface RouteCity {
  /** City id from the API — the key both legs are merged on. */
  id: string;
  name: string;
  lat: number;
  lng: number;
  /** Nights spent in this city. */
  cityNights: number;
  /** Nights aboard the ship here. 0 (with a `cruiseIndex`) = sails past without staying. */
  cruiseNights: number;
  kind: StopKind;
  /** Position in sailing order. Undefined when the ship never calls here. */
  cruiseIndex?: number;
}

type Mode = "all" | "cities" | "cruise";

const GOOGLE_KEY = process.env.NEXT_PUBLIC_GOOGLE_API_KEY!;
// AdvancedMarkerElement requires a Map ID; fall back to Google's demo id when
// none is configured (same convention as AgentMapView).
const MAP_ID = process.env.NEXT_PUBLIC_GOOGLE_MAP_ID || "DEMO_MAP_ID";

/**
 * Cruise-route colour. Far enough from the brand red that the two legs never read
 * as one route — but the dashes carry that meaning on their own, so the map
 * still works for anyone who can't separate the hues.
 */
const CRUISE_COLOR = "#0e7490";

/**
 * Top-down hull, bow toward -Y. Polyline icons default to `fixedRotation:
 * false`, so Google rotates this to match whichever edge it sits on — the ship
 * banks through every turn without us computing a single bearing.
 */
const SHIP_PATH =
  "M 0,-9 C 2,-6.4 3.4,-4.2 3.4,-2 L 3.4,5 L 2.4,8 L -2.4,8 L -3.4,5 L -3.4,-2 C -3.4,-4.2 -2,-6.4 0,-9 Z";

/** Index of the ship inside the cruise polyline's icon array (dashes are 0). */
const SHIP_ICON = 1;
/** One slow lap of the finished cruise route. A boat, not a courier. */
const SHIP_LOOP_MS = 15000;
/** Beat between the cities leg finishing and the ship setting off. */
const LEG_GAP_MS = 180;

const PAD_WIDE = { top: 110, right: 80, bottom: 60, left: 80 };
const PAD_COMPACT = { top: 92, right: 52, bottom: 48, left: 52 };

const nightsLabel = (n: number) => `${n} Night${n === 1 ? "" : "s"}`;

// ─── Pin element ─────────────────────────────────────────────────────
// Built imperatively because AdvancedMarkerElement takes a DOM node, not
// JSX. The hashed CSS-module class names still apply (the stylesheet is
// injected when this module is imported), so hover/active styling works.
//
// `showKind` stays false for packages with no cruise, which keeps their pins byte
// for byte what they were before the cruise leg existed.
function buildPin(
  city: RouteCity,
  index: number,
  mode: Mode,
  cityColor: string,
  showKind: boolean,
) {
  const pin = document.createElement("div");
  pin.className = `${styles.pin} ${styles.pinHidden}${
    showKind && city.kind === "both" ? ` ${styles.kindBoth}` : ""
  }`;
  // `--pin` drives the stem, dot and the focused card's animated border, so it
  // has to stay a plain colour. A dual-role stop splits via `--pin-city` /
  // `--pin-cruise` in the stylesheet instead.
  pin.style.setProperty("--pin", city.kind === "cruise" ? CRUISE_COLOR : cityColor);
  pin.style.setProperty("--pin-city", cityColor);
  pin.style.setProperty("--pin-cruise", CRUISE_COLOR);

  // Always-visible info card: counter number before the city name.
  const card = document.createElement("div");
  card.className = styles.stopCard;

  const head = document.createElement("div");
  head.className = styles.cardHead;

  const num = document.createElement("span");
  num.className = styles.num;
  num.textContent = String(index + 1);
  head.appendChild(num);

  const name = document.createElement("span");
  name.className = styles.city;
  name.textContent = city.name; // textContent → no HTML injection
  head.appendChild(name);

  card.appendChild(head);

  const addRow = (icon: string, tone: string, text: string) => {
    const row = document.createElement("span");
    row.className = styles.nights;
    if (showKind) {
      const glyph = document.createElement("i");
      glyph.className = `fa-solid ${icon} ${styles.nightsIcon} ${tone}`;
      glyph.setAttribute("aria-hidden", "true");
      row.appendChild(glyph);
    }
    const label = document.createElement("span");
    label.textContent = text;
    row.appendChild(label);
    card.appendChild(row);
  };

  // A dual-role stop can show both rows — that's the point of merging it.
  if (mode !== "cruise" && city.kind !== "cruise" && city.cityNights > 0) {
    addRow("fa-city", styles.toneCity, nightsLabel(city.cityNights));
  }
  if (mode !== "cities" && city.cruiseIndex != null) {
    // 0 cruise nights means the ship passes through without berthing here.
    addRow(
      "fa-ship",
      styles.toneCruise,
      city.cruiseNights > 0 ? nightsLabel(city.cruiseNights) : "Stopover",
    );
  }

  // Leader stem + dot anchored on the exact coordinate.
  const stem = document.createElement("div");
  stem.className = styles.stem;

  const dot = document.createElement("div");
  dot.className = styles.dot;

  pin.appendChild(card);
  pin.appendChild(stem);
  pin.appendChild(dot);
  return pin;
}

// ─── Map ──────────────────────────────────────────────────────────────
function RouteMapInner({
  cities,
  cruiseNights,
  compact,
}: {
  cities: RouteCity[];
  cruiseNights: number;
  compact: boolean;
}) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<google.maps.Map | null>(null);
  const markers = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);
  const pinEls = useRef<HTMLDivElement[]>([]);
  // First pass frames the route instantly (nobody is watching yet) and plays
  // the full-length draw; every later pass is a mode switch, so it tweens the
  // camera and draws faster — the user is waiting on it.
  const hasPlayed = useRef(false);

  const [mode, setMode] = useState<Mode>("all");
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  // Defer the map build + draw-in animation until the map is meaningfully
  // on screen, so the user actually witnesses it.
  const [started, setStarted] = useState(false);
  // Hover wins over selection for highlighting, so sweeping the legend
  // previews stops without losing the clicked one.
  const activeIdx = hoverIdx ?? selectedIdx;

  const hasCityStops = useMemo(
    () => cities.some((c) => c.kind !== "cruise"),
    [cities],
  );
  const hasCruiseStops = useMemo(
    () => cities.some((c) => c.kind !== "city"),
    [cities],
  );
  // Only offer the filter when there's actually something to filter, and only
  // colour-code pins once a cruise exists. Packages with no cruise render
  // exactly as they always have.
  const showToggle = hasCityStops && hasCruiseStops;
  const showKind = hasCruiseStops;

  const visible = useMemo(() => {
    if (mode === "cities") return cities.filter((c) => c.kind !== "cruise");
    if (mode === "cruise") return cities.filter((c) => c.kind !== "city");
    return cities;
  }, [cities, mode]);

  // The cities leg keeps API order; the cruise leg follows sailing order.
  const cityPath = useMemo(
    () => (mode === "cruise" ? [] : visible.filter((c) => c.kind !== "cruise")),
    [visible, mode],
  );
  const cruisePath = useMemo(
    () =>
      mode === "cities"
        ? []
        : visible
            .filter((c) => c.cruiseIndex != null)
            .sort((a, b) => a.cruiseIndex! - b.cruiseIndex!),
    [visible, mode],
  );

  // Toggle: clicking the active stop again clears the selection.
  const toggleSelect = (i: number) =>
    setSelectedIdx((prev) => (prev === i ? null : i));

  // Indices point into `visible`, so a mode change invalidates them.
  const switchMode = (next: Mode) => {
    setSelectedIdx(null);
    setHoverIdx(null);
    setMode(next);
  };

  const cityNightsShown = useMemo(
    () =>
      mode === "cruise"
        ? 0
        : visible.reduce((sum, c) => sum + (c.cityNights || 0), 0),
    [visible, mode],
  );
  const cruiseNightsShown = mode === "cities" ? 0 : cruiseNights;

  // Stable identity for the build effect: only re-init if the actual route
  // changes, not on every hover/select re-render.
  const routeKey = useMemo(
    () => cities.map((c) => `${c.id}:${c.lat},${c.lng}:${c.kind}`).join("|"),
    [cities],
  );

  // Hold the build (and its animation) until ≥30% of the map is visible.
  useEffect(() => {
    const host = mapRef.current;
    if (!host) return;
    if (typeof IntersectionObserver === "undefined") {
      setStarted(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (
          entries.some((e) => e.isIntersecting && e.intersectionRatio >= 0.3)
        ) {
          setStarted(true);
          io.disconnect();
        }
      },
      { threshold: [0.3] },
    );
    io.observe(host);
    return () => io.disconnect();
  }, []);

  // Create the map itself once. Pins and routes are rebuilt per mode below;
  // the camera and tiles survive a toggle, so switching never flashes grey.
  useEffect(() => {
    if (!started) return;
    const host = mapRef.current;
    if (!host || !cities.length || typeof google === "undefined") return;

    mapInstance.current = new google.maps.Map(host, {
      mapId: MAP_ID,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      zoomControl: true,
      clickableIcons: false,
      // Embedded in a long page → don't hijack page scroll.
      gestureHandling: "cooperative",
      backgroundColor: "#eef2f6",
    });
    hasPlayed.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeKey, started]);

  // Build the routes + pins for the current mode and play them in.
  useEffect(() => {
    if (!started) return;
    const map = mapInstance.current;
    const host = mapRef.current;
    if (!map || !host || !visible.length || typeof google === "undefined")
      return;

    const cityColor =
      getComputedStyle(host).getPropertyValue("--theme-color").trim() ||
      "#df1b23";
    const reduceMotion =
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
    const replay = hasPlayed.current;
    hasPlayed.current = true;
    // Each run owns its animations outright, so a mode switch tears its
    // predecessor down completely rather than leaving a loop running.
    let cancelDraw: (() => void) | undefined;
    let cancelFlow: (() => void) | undefined;
    let legTimer: number | undefined;

    // ── Pins ──────────────────────────────────────────────────────────
    const els: HTMLDivElement[] = [];
    const mks: google.maps.marker.AdvancedMarkerElement[] = [];
    visible.forEach((c, i) => {
      const content = buildPin(c, i, mode, cityColor, showKind);
      content.addEventListener("click", () => toggleSelect(i));
      content.addEventListener("mouseenter", () => setHoverIdx(i));
      content.addEventListener("mouseleave", () =>
        setHoverIdx((h) => (h === i ? null : h)),
      );
      mks[i] = new google.maps.marker.AdvancedMarkerElement({
        map,
        position: { lat: c.lat, lng: c.lng },
        content,
        title: c.name,
        zIndex: i,
      });
      els[i] = content;
    });
    pinEls.current = els;
    markers.current = mks;

    const indexById = new Map(visible.map((c, i) => [c.id, i]));
    const reveal = (c: RouteCity) => {
      const i = indexById.get(c.id);
      if (i != null) els[i]?.classList.remove(styles.pinHidden);
    };
    const revealAll = () =>
      els.forEach((e) => e?.classList.remove(styles.pinHidden));

    // ── Routes ────────────────────────────────────────────────────────
    const lines: google.maps.Polyline[] = [];
    const track = <T extends google.maps.Polyline>(p: T) => {
      lines.push(p);
      return p;
    };

    // White "casing" beneath a themed line keeps the route legible over busy
    // map areas; arrow symbols convey direction of travel.
    const cityCasing =
      cityPath.length > 1
        ? track(
            new google.maps.Polyline({
              path: [],
              geodesic: true,
              strokeColor: "#ffffff",
              strokeOpacity: 0.95,
              strokeWeight: 7,
              zIndex: 1,
              map,
            }),
          )
        : null;
    const cityLine =
      cityPath.length > 1
        ? track(
            new google.maps.Polyline({
              path: [],
              geodesic: true,
              strokeColor: cityColor,
              strokeOpacity: 1,
              strokeWeight: 4,
              zIndex: 2,
              map,
              icons: [
                {
                  icon: {
                    path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                    scale: 2.6,
                    fillColor: "#ffffff",
                    fillOpacity: 1,
                    strokeColor: "#ffffff",
                    strokeWeight: 1,
                  },
                  offset: "0%",
                  repeat: "120px",
                },
              ],
            }),
          )
        : null;

    // The cruise leg is dashed rather than solid: nautical-chart convention, and
    // it survives being read next to the red line in greyscale.
    const cruiseCasing =
      cruisePath.length > 1
        ? track(
            new google.maps.Polyline({
              path: [],
              geodesic: true,
              strokeColor: "#ffffff",
              strokeOpacity: 0.9,
              strokeWeight: 6,
              zIndex: 1,
              map,
            }),
          )
        : null;
    const cruiseLine =
      cruisePath.length > 1
        ? track(
            new google.maps.Polyline({
              path: [],
              geodesic: true,
              strokeColor: CRUISE_COLOR,
              // Dashes come from the repeated symbol, so the stroke itself
              // stays invisible.
              strokeOpacity: 0,
              strokeWeight: 4,
              zIndex: 3,
              map,
              icons: [
                {
                  icon: {
                    path: "M 0,-1 0,1",
                    strokeColor: CRUISE_COLOR,
                    strokeOpacity: 1,
                    strokeWeight: 4,
                    scale: 2.2,
                  },
                  offset: "0",
                  repeat: "13px",
                },
                {
                  icon: {
                    path: SHIP_PATH,
                    scale: compact ? 0.85 : 1,
                    fillColor: "#ffffff",
                    fillOpacity: 1,
                    strokeColor: CRUISE_COLOR,
                    strokeOpacity: 1,
                    strokeWeight: 1.6,
                  },
                  // Offsets are relative to the path *currently* set, so while
                  // the line grows the ship rides its tip for free — it draws
                  // the route rather than following one.
                  offset: "100%",
                },
              ],
            }),
          )
        : null;

    // ── Camera ────────────────────────────────────────────────────────
    const cancelCam = frameCamera(map, visible, compact, replay && !reduceMotion);

    // ── Play ──────────────────────────────────────────────────────────
    const startFlow = () => {
      if (reduceMotion) return;
      if (!cityLine && !cruiseLine) return;
      let raf: number | undefined;
      let flowStart: number | null = null;
      const flow = (ts: number) => {
        if (flowStart === null) flowStart = ts;
        if (cityLine) {
          const icons = cityLine.get("icons") as
            | google.maps.IconSequence[]
            | null;
          if (icons?.[0]) {
            icons[0].offset = `${(ts / 45) % 100}%`;
            cityLine.set("icons", icons);
          }
        }
        if (cruiseLine) {
          const icons = cruiseLine.get("icons") as
            | google.maps.IconSequence[]
            | null;
          const ship = icons?.[SHIP_ICON];
          if (ship) {
            const p = ((ts - flowStart) % SHIP_LOOP_MS) / SHIP_LOOP_MS;
            ship.offset = `${p * 100}%`;
            // Fade through the wrap so the ship dissolves and re-forms at the
            // first port instead of teleporting across the map.
            const fade = Math.min(1, Math.min(p, 1 - p) / 0.07);
            const sym = ship.icon as google.maps.Symbol;
            sym.fillOpacity = fade;
            sym.strokeOpacity = fade;
            cruiseLine.set("icons", icons!);
          }
        }
        raf = requestAnimationFrame(flow);
      };
      raf = requestAnimationFrame(flow);
      cancelFlow = () => {
        if (raf) cancelAnimationFrame(raf);
      };
    };

    const sailLeg = () => {
      if (!cruiseCasing || !cruiseLine) {
        if (cruisePath[0]) reveal(cruisePath[0]);
        revealAll();
        startFlow();
        return;
      }
      cancelDraw = animateDraw({
        casing: cruiseCasing,
        line: cruiseLine,
        path: cruisePath,
        // A touch slower than the cities leg — it's a boat.
        duration:
          Math.min(2600, 700 + (cruisePath.length - 1) * 560) *
          (replay ? 0.45 : 1),
        onReach: reveal,
        onDone: () => {
          revealAll();
          startFlow();
        },
      });
    };

    if (reduceMotion) {
      const set = (p: google.maps.Polyline | null, stops: RouteCity[]) =>
        p?.setPath(stops.map((c) => ({ lat: c.lat, lng: c.lng })));
      set(cityCasing, cityPath);
      set(cityLine, cityPath);
      set(cruiseCasing, cruisePath);
      set(cruiseLine, cruisePath);
      revealAll();
    } else if (cityCasing && cityLine) {
      // Cities first, then the ship — the map tells the trip in order rather
      // than racing two lines against each other.
      cancelDraw = animateDraw({
        casing: cityCasing,
        line: cityLine,
        path: cityPath,
        duration:
          Math.min(2400, 600 + (cityPath.length - 1) * 480) *
          (replay ? 0.45 : 1),
        onReach: reveal,
        onDone: () => {
          legTimer = window.setTimeout(sailLeg, LEG_GAP_MS);
        },
      });
    } else {
      if (cityPath[0]) reveal(cityPath[0]);
      sailLeg();
    }

    return () => {
      cancelDraw?.();
      cancelFlow?.();
      cancelCam();
      if (legTimer) clearTimeout(legTimer);
      mks.forEach((m) => m && (m.map = null));
      lines.forEach((p) => p.setMap(null));
      markers.current = [];
      pinEls.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeKey, started, mode, compact]);

  // Reflect the active stop on its pin: focus it, raise it above siblings,
  // and dim the rest so the cards never interfere with one another.
  useEffect(() => {
    pinEls.current.forEach((el, i) => {
      if (!el) return;
      el.classList.toggle(styles.pinActive, i === activeIdx);
      el.classList.toggle(styles.dimmed, activeIdx !== null && i !== activeIdx);
    });
    markers.current.forEach((m, i) => {
      if (m) m.zIndex = i === activeIdx ? 1000 : i;
    });
  }, [activeIdx]);

  // Pan to the clicked stop (hover-only previews don't move the map).
  useEffect(() => {
    const map = mapInstance.current;
    if (!map || selectedIdx === null) return;
    const stop = visible[selectedIdx];
    if (stop) map.panTo({ lat: stop.lat, lng: stop.lng });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIdx]);

  const stopWord = mode === "cruise" ? "Port" : "Stop";
  const modes: { id: Mode; icon: string; label: string }[] = [
    { id: "all", icon: "fa-route", label: "All" },
    { id: "cities", icon: "fa-city", label: "Cities" },
    { id: "cruise", icon: "fa-ship", label: "Cruise" },
  ];

  // Lives in the header rather than over the map: the canvas keeps every pixel
  // of its height, and a view control belongs next to the title anyway.
  // Deliberately not gated on `started` (unlike the on-map summary chip) — the
  // header is in normal flow, so revealing the filter later would reflow it.
  // Toggling before the map scrolls into view is harmless: the draw simply
  // starts in whichever mode is selected.
  const legFilter = showToggle ? (
    <div className={styles.modes} role="group" aria-label="Filter map by trip leg">
      <span
        className={`${styles.modeGlider} ${
          mode === "cruise" ? styles.modeGliderCruise : ""
        }`}
        style={{
          transform: `translateX(${modes.findIndex((m) => m.id === mode) * 100}%)`,
        }}
        aria-hidden="true"
      />
      {modes.map((m) => (
        <button
          key={m.id}
          type="button"
          title={m.label}
          className={`${styles.mode} ${mode === m.id ? styles.modeOn : ""}`}
          onClick={() => switchMode(m.id)}
          aria-pressed={mode === m.id}
          aria-label={m.label}
        >
          <i className={`fa-solid ${m.icon}`} aria-hidden="true" />
          <span className={styles.modeLabel}>{m.label}</span>
        </button>
      ))}
    </div>
  ) : null;

  return (
    <>
      {compact ? (
        // Sidebar widget: a modern titled header (the on-map summary chip
        // carries the stops/nights count).
        <div className={styles.sideHeader}>
          <span className={styles.headerIcon}>
            <i className="fa-solid fa-route" aria-hidden="true" />
          </span>
          <span className={styles.headerText}>
            <span className={styles.headerKicker}>Your Journey</span>
            <h3 className={styles.headerTitle}>Package Map</h3>
          </span>
          {legFilter}
        </div>
      ) : (
        <div className={styles.introRow}>
          <p className={styles.intro}>
            {visible.length > 1 ? (
              <>
                Follow the journey from <strong>{visible[0].name}</strong> to{" "}
                <strong>{visible[visible.length - 1].name}</strong> across{" "}
                {visible.length} {stopWord.toLowerCase()}s
                {cityNightsShown > 0
                  ? ` · ${nightsLabel(cityNightsShown)}`
                  : ""}
                . Hover or tap any point for details.
              </>
            ) : (
              <>
                Discover <strong>{visible[0]?.name}</strong> on the map.
              </>
            )}
          </p>
          {legFilter}
        </div>
      )}

      <div className={styles.card}>
        {started && (
          <div className={styles.summary} aria-hidden="true">
            <i className={`fa-solid fa-route ${styles.summaryIcon}`} />
            <span className={styles.summaryStat}>
              {visible.length}
              <small>
                {stopWord}
                {visible.length === 1 ? "" : "s"}
              </small>
            </span>
            {cityNightsShown > 0 && (
              <>
                <span className={styles.summaryDot} />
                <span className={styles.summaryStat}>
                  {cityNightsShown}
                  <small>Night{cityNightsShown === 1 ? "" : "s"}</small>
                </span>
              </>
            )}
            {cruiseNightsShown > 0 && (
              <>
                <span className={styles.summaryDot} />
                <span className={styles.summaryStat}>
                  {cruiseNightsShown}
                  <small>Night{cruiseNightsShown === 1 ? "" : "s"} Cruise</small>
                </span>
              </>
            )}
          </div>
        )}

        <div
          ref={mapRef}
          className={styles.mapCanvas}
          role="region"
          aria-label="Interactive trip route map"
        />

        {/* Until the map scrolls into view, keep the shimmer so the draw-in
            animation isn't missed. */}
        {!started && (
          <div className={styles.mapSkeleton} aria-hidden="true">
            <div className={styles.skeletonShimmer} />
          </div>
        )}

        {started && visible.length > 1 && (
          <div className={styles.legend}>
            {visible.map((c, i) => {
              const pos =
                i === 0
                  ? styles.nodeFirst
                  : i === visible.length - 1
                    ? styles.nodeLast
                    : "";
              // The dot follows the leg the stop is being shown as, so the
              // legend reads the same as the pins on the map.
              const tone =
                !showKind || c.kind === "city" || mode === "cities"
                  ? ""
                  : c.kind === "cruise" || mode === "cruise"
                    ? styles.nodeCruise
                    : styles.nodeBoth;
              const nights =
                mode === "cruise"
                  ? c.cruiseNights
                  : c.cityNights || c.cruiseNights;
              return (
                <button
                  key={c.id}
                  type="button"
                  title={c.name}
                  className={`${styles.node} ${pos} ${tone} ${
                    activeIdx === i ? styles.nodeActive : ""
                  }`}
                  onClick={() => toggleSelect(i)}
                  onMouseEnter={() => setHoverIdx(i)}
                  onMouseLeave={() => setHoverIdx((h) => (h === i ? null : h))}
                  aria-pressed={selectedIdx === i}
                  aria-label={`${c.name}${
                    nights > 0 ? `, ${nightsLabel(nights)}` : ""
                  }`}
                >
                  <span className={styles.nodeTop}>
                    <span className={`${styles.nodeLine} ${styles.nodeLineL}`} />
                    <span className={styles.nodeDot}>{i + 1}</span>
                    <span className={`${styles.nodeLine} ${styles.nodeLineR}`} />
                  </span>
                  <span className={styles.nodeName}>{c.name}</span>
                  {nights > 0 && (
                    <span className={styles.nodeNights}>{nights}</span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}

// ─── Animation helpers ───────────────────────────────────────────────

/**
 * Grow a polyline along `path`, popping each stop in as the line reaches it.
 * Legs are densified so the line appears to draw smoothly rather than snapping
 * from city to city. Returns a cancel function.
 */
function animateDraw({
  casing,
  line,
  path,
  duration,
  onReach,
  onDone,
}: {
  casing: google.maps.Polyline;
  line: google.maps.Polyline;
  path: RouteCity[];
  duration: number;
  onReach: (city: RouteCity) => void;
  onDone: () => void;
}): () => void {
  const pts = path.map((c) => ({ lat: c.lat, lng: c.lng }));
  const dense: google.maps.LatLngLiteral[] = [];
  const cityAt: number[] = [];
  const per = 22;
  for (let s = 0; s < pts.length - 1; s++) {
    cityAt[s] = dense.length;
    const a = pts[s];
    const b = pts[s + 1];
    for (let t = 0; t < per; t++) {
      const f = t / per;
      dense.push({
        lat: a.lat + (b.lat - a.lat) * f,
        lng: a.lng + (b.lng - a.lng) * f,
      });
    }
  }
  dense.push(pts[pts.length - 1]);
  cityAt[pts.length - 1] = dense.length - 1;

  const total = dense.length;
  let raf: number | undefined;
  let startTs: number | null = null;
  let next = 1;
  onReach(path[0]);

  const step = (ts: number) => {
    if (startTs === null) startTs = ts;
    const k = Math.min(1, (ts - startTs) / duration);
    const count = Math.max(2, Math.floor(k * total));
    const sub = dense.slice(0, count);
    casing.setPath(sub);
    line.setPath(sub);
    while (next < path.length && count - 1 >= cityAt[next]) onReach(path[next++]);
    if (k < 1) {
      raf = requestAnimationFrame(step);
    } else {
      path.forEach(onReach);
      onDone();
    }
  };
  raf = requestAnimationFrame(step);
  return () => {
    if (raf) cancelAnimationFrame(raf);
  };
}

const easeInOutCubic = (t: number) =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

/**
 * Frame `stops`. The first pass snaps (nothing is on screen yet); a mode switch
 * glides, because the reframe is the moment the user is actually watching.
 *
 * The Maps SDK has no animated `fitBounds`, so we let it compute the target,
 * read the camera back, rewind to where we were and tween across ourselves.
 * All of that happens inside one task, so the browser never paints the snap.
 * `mapId` makes this a vector map, where `moveCamera` accepts fractional zoom.
 *
 * Returns a cancel function.
 */
function frameCamera(
  map: google.maps.Map,
  stops: RouteCity[],
  compact: boolean,
  animate: boolean,
): () => void {
  const pad = compact ? PAD_COMPACT : PAD_WIDE;
  const applyFit = () => {
    if (stops.length === 1) {
      map.setCenter({ lat: stops[0].lat, lng: stops[0].lng });
      map.setZoom(9);
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    stops.forEach((s) => bounds.extend({ lat: s.lat, lng: s.lng }));
    map.fitBounds(bounds, pad);
  };
  // Stop tightly-clustered stops from zooming in too far.
  const capZoom = () => {
    const z = map.getZoom();
    if (z && z > 12) map.setZoom(12);
  };

  const from = map.getCenter()?.toJSON();
  const fromZoom = map.getZoom();

  const noop = () => {};

  if (!animate || !from || fromZoom == null) {
    applyFit();
    if (stops.length > 1) {
      const listener = google.maps.event.addListenerOnce(map, "idle", capZoom);
      return () => google.maps.event.removeListener(listener);
    }
    return noop;
  }

  applyFit();
  capZoom();
  const to = map.getCenter()?.toJSON();
  const toZoom = map.getZoom();
  // If the SDK deferred the fit, `to` still reads as `from`; leave the pending
  // fit alone rather than rewinding into a fight with it.
  if (
    !to ||
    toZoom == null ||
    (Math.abs(to.lat - from.lat) < 1e-6 &&
      Math.abs(to.lng - from.lng) < 1e-6 &&
      Math.abs(toZoom - fromZoom) < 1e-3)
  ) {
    return noop;
  }

  map.moveCamera({ center: from, zoom: fromZoom });
  let raf: number | undefined;
  let startTs: number | null = null;
  const step = (ts: number) => {
    if (startTs === null) startTs = ts;
    const k = Math.min(1, (ts - startTs) / 620);
    const e = easeInOutCubic(k);
    map.moveCamera({
      center: {
        lat: from.lat + (to.lat - from.lat) * e,
        lng: from.lng + (to.lng - from.lng) * e,
      },
      zoom: fromZoom + (toZoom - fromZoom) * e,
    });
    if (k < 1) raf = requestAnimationFrame(step);
  };
  raf = requestAnimationFrame(step);
  return () => {
    if (raf) cancelAnimationFrame(raf);
  };
}

function renderStatus(status: Status) {
  if (status === Status.FAILURE) {
    return (
      <div className={`${styles.card} ${styles.fallback}`}>
        <i className="fa-solid fa-triangle-exclamation" />
        <span>Map couldn&rsquo;t be loaded right now.</span>
      </div>
    );
  }
  return (
    <div className={`${styles.card} ${styles.skeleton}`} aria-hidden="true">
      <div className={styles.skeletonShimmer} />
    </div>
  );
}

export default function PackageRouteMap({
  cities,
  cruiseNights = 0,
  compact = false,
}: {
  cities: RouteCity[];
  /** Nights aboard a cruise, shown as an extra summary stat. 0 = hidden. */
  cruiseNights?: number;
  /** Sidebar mode: shorter map, condensed cards, titled header. */
  compact?: boolean;
}) {
  if (!cities.length) return null;
  return (
    <div className={compact ? styles.compact : undefined}>
      <Wrapper apiKey={GOOGLE_KEY} libraries={["marker"]} render={renderStatus}>
        <RouteMapInner
          cities={cities}
          cruiseNights={cruiseNights}
          compact={compact}
        />
      </Wrapper>
    </div>
  );
}
